import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getProjectSchedule, updateProjectSchedule } from '../../../../../helpers/project-helper';

const DAY_FIELDS = [
    'day_monday',
    'day_tuesday',
    'day_wednesday',
    'day_thursday',
    'day_friday',
    'day_saturday',
    'day_sunday',
];

const buildWorkingHours = (start, end) => {
    if (!start || !end) return 0;

    const [startHour, startMinute] = String(start).split(':').map(Number);
    const [endHour, endMinute] = String(end).split(':').map(Number);

    if ([startHour, startMinute, endHour, endMinute].some((value) => Number.isNaN(value))) {
        return 0;
    }

    const startTotal = (startHour * 60) + startMinute;
    const endTotal = (endHour * 60) + endMinute;

    if (endTotal <= startTotal) return 0;

    const diffHours = (endTotal - startTotal) / 60;
    return Number(diffHours.toFixed(2));
};

const mapScheduleToForm = (schedule = {}) => {
    return DAY_FIELDS.reduce((accumulator, dayField) => {
        const day = schedule?.[dayField] || {};

        return {
            ...accumulator,
            [dayField]: {
                active: Boolean(day.active),
                start: day.start || '09:00',
                end: day.end || '18:00',
                workingHours: Number(day.workingHours) || 0,
                name: day.name || '',
                id: day.id || '',
            },
        };
    }, {});
};

export const ProjectScheduleTabContent = ({ projectId }) => {
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [scheduleForm, setScheduleForm] = React.useState({});

    React.useEffect(() => {
        const loadSchedule = async () => {
            if (!token || !projectId) {
                setError('No se pudo obtener el horario del proyecto.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getProjectSchedule(token, projectId);
            if (!resp?.success) {
                setError(resp?.message || 'No se pudo obtener el horario del proyecto.');
                setIsLoading(false);
                return;
            }

            setScheduleForm(mapScheduleToForm(resp.data || {}));
            setIsLoading(false);
        };

        loadSchedule();
    }, [token, projectId]);

    const handleChangeDayField = (dayField, fieldName, value) => {
        setScheduleForm((prev) => {
            const currentDay = prev?.[dayField] || {};
            const nextDay = {
                ...currentDay,
                [fieldName]: value,
            };

            if (fieldName === 'start' || fieldName === 'end' || fieldName === 'active') {
                nextDay.workingHours = nextDay.active
                    ? buildWorkingHours(nextDay.start, nextDay.end)
                    : 0;
            }

            return {
                ...prev,
                [dayField]: nextDay,
            };
        });
    };

    const handleSaveSchedule = async () => {
        if (!token || !projectId || isSaving) return;

        setIsSaving(true);
        setError('');
        const savingToastId = toast.loading('Guardando horario del proyecto...');

        const payload = DAY_FIELDS.reduce((accumulator, dayField) => {
            const day = scheduleForm?.[dayField];

            if (!day) return accumulator;

            return {
                ...accumulator,
                [dayField]: {
                    ...day,
                    workingHours: day.active ? buildWorkingHours(day.start, day.end) : 0,
                },
            };
        }, {});

        const resp = await updateProjectSchedule(token, projectId, payload);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar el horario del proyecto.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSaving(false);
            return;
        }

        setScheduleForm(mapScheduleToForm(resp.data || {}));
        toast.update(savingToastId, {
            render: resp?.message || 'Horario actualizado correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSaving(false);
    };

    return (
        <div className="profile-details mb-1">
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <h5 className="fw-bold mb-0">Horario del proyecto</h5>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveSchedule}
                    disabled={isSaving || isLoading}
                >
                    {isSaving ? 'Guardando...' : 'Guardar horario'}
                </button>
            </div>

            {isLoading && <div className="py-3 text-muted">Cargando horario...</div>}

            {!isLoading && error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            {!isLoading && !error && (
                <div className="table-responsive border border-gray-3 rounded-2">
                    <table className="table table-hover mb-0">
                        <thead>
                            <tr>
                                <th className="text-uppercase fs-11 fw-semibold py-3">Día</th>
                                <th className="text-uppercase fs-11 fw-semibold py-3">Activo</th>
                                <th className="text-uppercase fs-11 fw-semibold py-3">Inicio</th>
                                <th className="text-uppercase fs-11 fw-semibold py-3">Fin</th>
                                <th className="text-uppercase fs-11 fw-semibold py-3">Horas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {DAY_FIELDS.map((dayField) => {
                                const day = scheduleForm?.[dayField] || {};

                                return (
                                    <tr key={dayField}>
                                        <td className="py-2 fw-semibold">{day.name || '-'}</td>
                                        <td className="py-2">
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={Boolean(day.active)}
                                                    onChange={(event) => handleChangeDayField(dayField, 'active', event.target.checked)}
                                                />
                                            </div>
                                        </td>
                                        <td className="py-2">
                                            <input
                                                type="time"
                                                className="form-control form-control-sm"
                                                value={day.start || '09:00'}
                                                onChange={(event) => handleChangeDayField(dayField, 'start', event.target.value)}
                                                disabled={!day.active}
                                            />
                                        </td>
                                        <td className="py-2">
                                            <input
                                                type="time"
                                                className="form-control form-control-sm"
                                                value={day.end || '18:00'}
                                                onChange={(event) => handleChangeDayField(dayField, 'end', event.target.value)}
                                                disabled={!day.active}
                                            />
                                        </td>
                                        <td className="py-2">{day.active ? (day.workingHours || 0) : 0}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
